// « Compléter le projet » : l'ampoule du menu et ses cartes.
//
// Le parcours de démarrage ne demande que l'état des lieux. Tout le reste de la
// mesure zéro se fait ICI, à son rythme : une carte par étape, qui dit si elle
// est faite, et qui ouvre l'assistant sur cette seule étape.
//
// L'état « fait » est DÉDUIT du contexte renvoyé par le serveur (comme
// l'avancement de l'assistant) : jamais un drapeau qui pourrait mentir.
import { useCallback, useState } from 'react';
import { useProductContext } from '../api/queries';
import type { ProductContext } from '../api/types';
import { Icon, type IconName } from '../components/icons';
import type { Hue } from '../taxonomy/colors';
import { Drawer } from '../write/Drawer';
import { useCanWrite } from '../write/useCanWrite';
import { Mesure0Wizard, type StepId } from './Mesure0Wizard';
import './mesure0.css';

interface Card {
  id: StepId;
  title: string;
  text: string;
  icon: IconName;
  hue: Hue;
  done: (c: ProductContext) => boolean;
}

// Dans l'ordre où on les ferait : du plus simple au plus construit.
const CARDS: Card[] = [
  {
    id: 'product',
    title: 'État des lieux',
    text: 'Où en est le produit, son type et à qui il s’adresse.',
    icon: 'eye',
    hue: 'cyan',
    done: (c) => c.steps.product,
  },
  {
    id: 'questionnaire',
    title: 'Questionnaire',
    text: 'Dix questions pour cadrer le besoin, à remplir ou à envoyer.',
    icon: 'checklist',
    hue: 'violet',
    done: (c) => c.questionnaire.clientResponses + c.questionnaire.internalAnswered > 0,
  },
  {
    id: 'features',
    title: 'Fonctionnalités',
    text: 'Ce que le produit permet de faire.',
    icon: 'click',
    hue: 'blue',
    done: (c) => c.steps.features,
  },
  {
    id: 'roles',
    title: 'Utilisateurs',
    text: 'Les métiers qui utilisent le produit, et combien ils sont.',
    icon: 'users',
    hue: 'pink',
    done: (c) => c.steps.roles,
  },
  {
    id: 'diagnosis',
    title: 'Bilan',
    text: 'Ce qui fonctionne, et ce qui ne fonctionne pas.',
    icon: 'audit',
    hue: 'amber',
    done: (c) => c.product.works.length + c.product.fails.length > 0,
  },
  {
    id: 'pillars',
    title: 'Piliers d’adoption',
    text: 'Les piliers de votre modèle, à noter tels que vous les percevez.',
    icon: 'gauge',
    hue: 'green',
    done: (c) => c.steps.pillars,
  },
];

/** Où en est le contexte du projet : `null` pour qui ne peut pas écrire. */
export function useProjectTodo(projectId: string) {
  const context = useProductContext(projectId);
  const canWrite = useCanWrite(projectId);
  if (!canWrite || !context.data) return null;
  const doneCount = CARDS.filter((card) => card.done(context.data)).length;
  return { context: context.data, doneCount, total: CARDS.length, remaining: CARDS.length - doneCount };
}

/**
 * L'AMPOULE du menu, à côté de la roue crantée : ce qu'il reste à définir,
 * avec le nombre d'étapes restantes. Un clic ouvre les cartes dans un panneau ;
 * une carte ouvre l'assistant sur cette seule étape, dans le même panneau.
 */
export function ProjectTodoButton({ projectId, projectName }: { projectId: string; projectName: string }) {
  const todo = useProjectTodo(projectId);
  const [panel, setPanel] = useState(false);
  const [open, setOpen] = useState<Card | null>(null);
  // Identité STABLE : le panneau relance sa gestion du focus à chaque changement
  // de `onClose`.
  const close = useCallback(() => { setPanel(false); setOpen(null); }, []);

  // Rien à proposer à qui ne peut pas écrire, ni tant que le contexte manque.
  if (!todo) return null;
  const c = todo.context;
  const label = todo.remaining > 0
    ? `Recommandations : ${todo.remaining} étape${todo.remaining > 1 ? 's' : ''} à définir`
    : 'Le contexte du projet est complet';

  return (
    <>
      <button
        type="button"
        className={`nav-gear nav-bulb${todo.remaining > 0 ? ' nav-bulb-on' : ''}`}
        data-tour="todo"
        aria-label={label}
        title={label}
        onClick={() => setPanel(true)}
      >
        <Icon name="bulb" size={17} />
        {todo.remaining > 0 ? <span className="nav-bulb-n" aria-hidden="true">{todo.remaining}</span> : null}
      </button>
      <Drawer
        open={panel}
        title={open ? open.title : todo.remaining > 0 ? 'Ce qu’il reste à définir' : 'Le contexte du projet est complet'}
        description={open ? open.text : `${todo.doneCount} étape${todo.doneCount > 1 ? 's' : ''} sur ${todo.total} faite${todo.doneCount > 1 ? 's' : ''}. Chaque carte ouvre l’assistant sur cette seule étape.`}
        onClose={close}
        wide
      >
        {open ? (
          <>
            <button type="button" className="zy-retour todo-back" onClick={() => setOpen(null)}>
              ← Toutes les étapes
            </button>
            <Mesure0Wizard
              key={open.id}
              projectId={projectId}
              projectName={projectName}
              context={c}
              only={open.id}
              onClose={() => setOpen(null)}
            />
          </>
        ) : (
          <ul className="todo-grid">
            {CARDS.map((card) => {
              const done = card.done(c);
              return (
                <li key={card.id}>
                  <button
                    type="button"
                    className={`todo-card hue-${card.hue}${done ? ' todo-card-done' : ''}`}
                    onClick={() => setOpen(card)}
                  >
                    <span className="todo-icon" aria-hidden="true">
                      <Icon name={card.icon} size={22} />
                    </span>
                    <span className="todo-title">{card.title}</span>
                    <span className="todo-text">{card.text}</span>
                    <span className="todo-status">
                      {done ? '✓ Fait · modifier' : 'À définir →'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Drawer>
    </>
  );
}
